/* eslint-disable no-underscore-dangle */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Modal, Button, Card, Form, Row, Col, InputGroup,
} from 'react-bootstrap';
// The operator picker. A bare <select> of the scoped list was unsearchable —
// you had to know the plate to find the row. Same component, same two CSS
// imports as the month-end counterparty filter (Pages/MonthEnd/index.jsx).
import {
  Typeahead, Menu, MenuItem, Highlighter,
} from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';
import _ from 'lodash';
import Error from '../../../Components/Error';
import OrderLinesService from '../../../Services/OrderLinesService';
import OperatorService from '../../../Services/OperatorService';
import setStateFromInput from '../../../Utils/setStateFromInput';
import { formatOrderLineRole as formatRole } from '../../../Utils/orderLineKinds';
// The ledger itself — what this tab shows above the form.
import OrderLedger from './OrderLedger';
// The rules the editing runs on. Pure, tested on their own, and mirrored
// server-side — see Utils/orderLineAdjustments.js.
import {
  bookingOperatorsById,
  describeLocks,
  findDuplicateAdjustment,
  formToAdjustmentLine,
  scopeOperatorChoices,
  describeConsequences,
} from '../../../Utils/orderLineAdjustments';

// The currencies an adjustment can be written in. A booking in anything else
// gets its own currency prepended — see `currencyOptions`.
const BASE_CURRENCIES = ['EUR', 'HUF'];

// The two kinds of thing the operator picker offers, in menu order. The
// wording is unchanged from the <optgroup> labels it replaces: telling an
// operator working this booking apart from a cost bucket for one of its
// vehicles is the whole point of the grouping, and a searchable box that
// dropped the distinction would be a worse picker, not a better one.
const PICKER_GROUPS = [
  { key: 'onBooking', label: 'On this booking' },
  { key: 'costBuckets', label: "Cost buckets for this booking's vehicles" },
];

/**
 * Order Details' "Order Ledger" tab: the booking's ledger (OrderLedger), the
 * form that adds or corrects a manual adjustment, and each adjustment's
 * edit / delete / reverse. Mounted while the tab is open, so the ledger is
 * read fresh every time it is.
 *
 *   - `order`        the booking Details holds
 *   - `payments`     its Payment rows (Details' useBookingPayments), or null
 *                    while they are not known. What is paid and still due
 *                    needs the ledger too, so OrderLedger gets them only
 *                    once both are in — never a €0.00 that means "loading".
 *   - `readOnly`     the ledger alone: no form, no actions (Month End's
 *                    drill-down)
 *   - `prefill`      starting values for the form, handed off from another
 *                    screen (e.g. a driver's approved parking receipt). Only
 *                    seeds the form — nothing is saved until the admin
 *                    presses "Add adjustment". `prefillNote` explains them.
 *   - `onChanged()`  after every write that landed
 */
function OrderLedgerTab({
  order, payments = null, readOnly = false, prefill = null, prefillNote = '',
  onChanged = () => {},
}) {
  // The booking's own unit. 2,299 bookings carry no `currency` at all, so the
  // `|| 'EUR'` is the same convention the rest of this file uses. Declared up
  // here because the add form defaults to it — see defaultNewLine below.
  const bookingCurrency = order.currency || 'EUR';
  // What the currency picker may offer. The booking's own currency is always
  // one of them: a default the box cannot display would render blank and the
  // line would be saved in whatever the user then picked by accident.
  const currencyOptions = useMemo(
    () => (BASE_CURRENCIES.includes(bookingCurrency)
      ? BASE_CURRENCIES
      : [bookingCurrency, ...BASE_CURRENCIES]),
    [bookingCurrency],
  );

  const [orderLines, setOrderLines] = useState([]);
  const [balanceByOperator, setBalanceByOperator] = useState([]);
  // order_line_key -> the payments / invoices that reference it, computed by
  // the API from Payment.lines[] and Invoice.lines[]. It is the ONLY thing
  // that decides whether a line may be edited: a booking's status is not
  // evidence that its money has moved.
  const [locks, setLocks] = useState({});
  // Whether the ledger fetch has completed at least once. `orderLines` is the
  // ONLY live view of what is on the booking — submitting before it lands
  // would rebuild the adjustment set from nothing and wipe existing lines.
  const [linesLoaded, setLinesLoaded] = useState(false);
  // `totals.balance` from the API used to feed an aggregate
  // "Outstanding" panel. The two-sided partner/operator panels below
  // recompute their own sums directly from `orderLines` using the
  // FX-converted-to-booking-currency fields, so the netted aggregate
  // is no longer needed in component state.

  // Form model is intentionally simpler than the persisted line shape.
  // The user thinks in two signed numbers — "what changes for the
  // customer" and "what changes for the operator" — instead of three
  // accounting fields (debit / credit / credit_operator). We map to the
  // schema on submit.
  const defaultNewLine = {
    customer_adjustment: 0, // + = charge more, - = refund
    operator_adjustment: 0, // + = we owe operator more, - = operator settled with us
    // The BOOKING's currency, not EUR. A hardcoded EUR meant every
    // adjustment on a HUF booking started in the wrong unit, which is how
    // an 800 HUF parking cost gets saved as 800 EUR and a booking's total
    // reads −746.79. A booking with no currency field still falls back to
    // EUR, the same convention the rest of this file uses.
    currency: bookingCurrency,
    description: '',
    // '' = booking-wide adjustment (no operator attribution). Required
    // when operator_adjustment !== 0 so the per-operator summary can
    // attribute the change.
    target_operator_id: '',
  };
  const [newLine, setNewLine] = useState(
    prefill ? { ...defaultNewLine, ...prefill } : defaultNewLine,
  );

  // The adjustment being edited (its `_id`), or null when the form is adding.
  const [editingId, setEditingId] = useState(null);
  // The line a reversal is being written for, plus the typed reason.
  const [reversing, setReversing] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  // The line a delete is being confirmed for.
  const [deleting, setDeleting] = useState(null);
  // Every operator, used ONLY to find this booking's cost buckets by name.
  // Never rendered as a list — see scopeOperatorChoices.
  const [allOperators, setAllOperators] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // The adjustment form, and its first box — so starting an edit can scroll
  // to the form and land the cursor in it rather than changing something
  // the person cannot see.
  const editFormRef = useRef(null);
  const editFirstFieldRef = useRef(null);

  const fetchOrderLines = async () => {
    setLoading(true);
    try {
      const data = await OrderLinesService.get(order._id);
      if (data) {
        setOrderLines(data.lines || []);
        // Per-operator balances the API already converted into EACH
        // operator's own settlement currency (read from their Dynamo
        // record). This is the figure we actually pay them, so the
        // per-operator summary reports its Net in these units rather
        // than re-deriving one from the booking-currency line values.
        setBalanceByOperator(data.balance_by_operator || []);
        // Absent on an API older than the locks field. An empty map means
        // "nothing is known to be settled", which leaves the actions
        // available — the server refuses anything it should not allow, so
        // the screen is never the only thing standing in the way.
        setLocks(data.locks || {});
        setLinesLoaded(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchOrderLines = _.debounce(fetchOrderLines, 500);

  useEffect(() => {
    debouncedFetchOrderLines();
    return () => debouncedFetchOrderLines.cancel();
  }, []);

  // Every operator, fetched once when the tab opens so the picker can
  // resolve this booking's cost buckets BY NAME (`AU-AA-670 Parkolás`).
  //
  // There ARE now fields for this, and this picker does not use them:
  // `Car.cost_operators` maps a vehicle's parking/fuel/rental/other buckets
  // BY ID, and `operator_type` ('operator' | 'expense', see
  // Utils/operatorType.js) says outright which records are cost buckets.
  // Between them they answer by id what the plate regex here infers from
  // spelling. But scopeOperatorChoices still matches on the name —
  // `classifyOperator`/`vehicleOf` over `AU-AA-670 Parkolás` — so a bucket
  // renamed out of that shape drops silently out of this menu. Moving it to
  // the ids is a change of behaviour and is deliberately not made here.
  //
  // A failure here just means no buckets are offered, never a broken form.
  const fetchAllOperators = async () => {
    if (readOnly) return;
    try {
      const response = await OperatorService.fetchOperators();
      const rows = response?.data?.data ?? response?.data ?? [];
      setAllOperators(Array.isArray(rows) ? rows : []);
    } catch {
      setAllOperators([]);
    }
  };

  useEffect(() => {
    fetchAllOperators();
    // fetchAllOperators is defined inline above and stable in semantics —
    // we don't want to memoize it just to satisfy the deps lint rule.
  }, [readOnly]); // eslint-disable-line

  // The booking's operators, for attributing an adjustment to one.
  const operatorById = useMemo(
    () => bookingOperatorsById(order),
    [order.operators, order.operator_id, order.operator],
  );

  const hasOperators = (order.operators?.length ?? 0) > 0 || !!order.operator_id;

  // What the picker may offer: the booking's own operators, plus the cost
  // buckets belonging to the vehicles OUR OWN drivers on it are running.
  // Never all 130 — picking one of those by hand is how the money landed on
  // the wrong record. A third-party operator brings no buckets: it has none,
  // and its expenses are filed against the operator itself, which is already
  // in the list.
  const operatorChoices = useMemo(() => scopeOperatorChoices({
    bookingOperators: order.operators?.length
      ? order.operators
      : [...operatorById.values()],
    allOperators,
  }), [order.operators, operatorById, allOperators]);

  // The same scoped set, flattened into one array carrying a `label` string —
  // the shape react-bootstrap-typeahead matches on. `group` rides along so the
  // menu can still be split under its two headings.
  const operatorOptions = useMemo(() => ([
    ...operatorChoices.onBooking.map((op) => ({
      ...op,
      group: 'onBooking',
      label: `${op.name}${op.role ? ` · ${formatRole(op.role)}` : ''}`,
    })),
    ...operatorChoices.costBuckets.map((op) => ({
      ...op,
      group: 'costBuckets',
      label: `${op.name} · ${op.costType} cost`,
    })),
  ]), [operatorChoices]);

  /** The option currently picked, or null. Drives the controlled Typeahead. */
  const selectedOperatorOption = useMemo(() => {
    const id = newLine.target_operator_id;
    if (!id) return null;
    return operatorOptions.find((op) => String(op.id) === String(id)) || null;
  }, [newLine.target_operator_id, operatorOptions]);

  /** The name to say in the consequence sentence for the picked operator. */
  const chosenOperatorName = selectedOperatorOption?.name || null;

  /**
   * The picker's menu, grouped. react-bootstrap-typeahead has no <optgroup>
   * equivalent, so the headings are rendered by hand — dropping them would
   * make a cost bucket indistinguishable from a real operator at the moment
   * of choosing, which is the mistake the scoping exists to prevent.
   *
   * `position` must run unbroken across ALL items regardless of grouping:
   * it is what keyboard navigation and selection are keyed on.
   */
  const renderOperatorMenu = (results, menuProps, { text }) => {
    const {
      newSelectionPrefix, paginationText, renderMenuItemChildren, ...rest
    } = menuProps;
    let position = 0;
    return (
      // The menu's own props (id, aria wiring, positioning ref) come from the
      // library and are passed straight through.
      // eslint-disable-next-line react/jsx-props-no-spreading
      <Menu {...rest}>
        {PICKER_GROUPS.flatMap(({ key, label }) => {
          const inGroup = results.filter((option) => option.group === key);
          if (inGroup.length === 0) return [];
          return [
            <Menu.Header key={`group-${key}`}>{label}</Menu.Header>,
            ...inGroup.map((option) => {
              // `label` is what the row is ANNOUNCED as. Without it the
              // highlighted fragments are all a screen reader gets, because
              // the match is wrapped in <mark> as you type.
              const item = (
                <MenuItem
                  key={option.id}
                  option={option}
                  position={position}
                  label={option.label}
                >
                  <Highlighter search={text}>{option.label}</Highlighter>
                </MenuItem>
              );
              position += 1;
              return item;
            }),
          ];
        })}
      </Menu>
    );
  };

  // Reload the ledger after any write. The server rebuilds order_lines on
  // save, so the only honest view is a refetch.
  //
  // This used to wait 2s on a `setTimeout` and then call the DEBOUNCED fetch,
  // which adds its own 500ms — so the line you just added took two and a half
  // seconds to appear, behind a spinner, and the write looked like it had not
  // landed. The 2s was a hangover from the whole-booking PUT, which returned
  // before the booking had been rebuilt. Writes now go to the dedicated
  // order-lines endpoint and have resolved by the time we get here, so refetch
  // straight away and use the UNDEBOUNCED fetch. `fetchOrderLines` owns
  // `loading` for the duration, so the spinner stops on the render that puts
  // the new line on screen rather than some fixed time before it.
  const afterWrite = async () => {
    setNewLine(defaultNewLine);
    setEditingId(null);
    setReversing(null);
    setReverseReason('');
    setDeleting(null);
    onChanged();
    try {
      await fetchOrderLines();
    } catch {
      // The write itself succeeded — only the re-read failed. Saying
      // "failed to add" here would be a lie that invites a second attempt
      // and a duplicate line.
      setError('Saved, but the ledger could not be reloaded — reopen the booking to see it.');
    }
  };

  const failWith = (err, fallback) => {
    setLoading(false);
    setError(err?.response?.data?.message || err?.message || fallback);
  };

  /**
   * Add a new adjustment, or save the one being edited.
   *
   * Both go through the dedicated order-lines endpoint rather than a
   * whole-booking PUT. That matters beyond tidiness: the PUT rewrote the
   * ENTIRE adjustment array from whatever this screen happened to be
   * holding, so a stale copy could drop a line somebody else had added
   * (PU_DASH_7126216 lost a 15,246 HUF parking line that way) and nothing
   * could stop a settled line being removed. The server now appends to, or
   * edits within, the booking's own lines and refuses what it must.
   */
  const handleSubmit = async () => {
    const customerAdj = Number(newLine.customer_adjustment) || 0;
    const operatorAdj = Number(newLine.operator_adjustment) || 0;

    // Q1 guard: an operator-side change without a designated operator
    // would land in the company-wide operator-cost total but wouldn't
    // appear on anyone's row. Block it so the admin picks who it's for.
    if (operatorAdj !== 0 && !newLine.target_operator_id && hasOperators) {
      setError('Operator amount adjustment requires picking an operator to attribute it to.');
      return;
    }
    if (customerAdj === 0 && operatorAdj === 0) {
      setError('Enter a customer or operator amount before saving.');
      return;
    }
    if (!newLine.description?.trim()) {
      setError('Add a short description so the audit trail explains why.');
      return;
    }
    if (!linesLoaded) {
      setError('Still loading the booking ledger — try again in a moment.');
      return;
    }

    // Refuse an identical line before it is sent. PU_DASH_3730272 carries
    // the same +800 HUF parking adjustment twice; a repeated click and a
    // second person doing the same job both look exactly like this. A
    // genuinely different line — another car, another description — is
    // unaffected. The server checks again and is the one that decides.
    const candidate = formToAdjustmentLine(newLine, operatorById);
    if (findDuplicateAdjustment(orderLines, candidate, { ignoreId: editingId })) {
      setError(`Already added — this booking already has "${candidate.description}" with the same amount, currency and operator.`);
      return;
    }

    const input = {
      description: newLine.description.trim(),
      customerAmount: customerAdj,
      operatorAmount: operatorAdj,
      operatorId: newLine.target_operator_id || undefined,
      role: newLine.target_operator_id
        ? operatorById.get(newLine.target_operator_id)?.role
        : undefined,
      currency: newLine.currency,
    };

    setLoading(true);
    try {
      if (editingId) await OrderLinesService.updateAdjustment(order._id, editingId, input);
      else await OrderLinesService.addAdjustment(order._id, input);
      await afterWrite();
    } catch (err) {
      failWith(err, editingId ? 'Failed to save the adjustment' : 'Failed to add adjustment');
    }
  };

  /**
   * Load a line back into the form. Only ever reached on an unlocked one.
   *
   * The form is a long way down a tall tab, so without this the pencil
   * looks like it does nothing: the card's heading changes from "Add" to
   * "Edit" off-screen and the row you clicked gives no sign it is the one
   * being edited. Scroll to the form, put the cursor in it, and mark the
   * row — the click has to be visibly answered.
   */
  const handleStartEdit = (line) => {
    setError('');
    setEditingId(String(line._id));

    // After the re-render that fills the form, not before it.
    window.requestAnimationFrame(() => {
      editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      editFirstFieldRef.current?.focus();
    });
    const customer = (Number(line.debit) || 0) - (Number(line.credit) || 0);
    setNewLine({
      customer_adjustment: customer,
      operator_adjustment: Number(line.credit_operator) || 0,
      currency: line.currency || bookingCurrency,
      description: line.description || '',
      target_operator_id: line.metadata?.operator_id || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setNewLine(defaultNewLine);
    setError('');
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setLoading(true);
    try {
      await OrderLinesService.deleteAdjustment(order._id, String(deleting._id));
      await afterWrite();
    } catch (err) {
      failWith(err, 'Failed to delete the adjustment');
    }
  };

  /**
   * Write the paired negative for a settled line.
   *
   * The server builds it — same amount, operator and currency, linked to the
   * original — so a correction can never again be a hand-typed "-800" that
   * reads exactly like a legitimate credit.
   */
  const handleReverse = async () => {
    if (!reversing) return;
    if (!reverseReason.trim()) {
      setError('A reversal needs a reason — it is what the struck-through row says.');
      return;
    }
    setLoading(true);
    try {
      const reason = reverseReason.trim();
      await OrderLinesService.reverseAdjustment(order._id, String(reversing._id), reason);
      await afterWrite();
    } catch (err) {
      failWith(err, 'Failed to reverse the adjustment');
    }
  };

  // An unreversed adjustment's buttons. Unlocked: correct it or remove it.
  // Paid or billed: the amount may no longer move, but the correction still
  // has to be possible — reversal writes the paired negative itself instead
  // of leaving it to be typed.
  const renderLineActions = (line, lineLocks) => (lineLocks.length === 0 ? (
    <>
      <Button
        size="sm"
        variant="outline-secondary"
        className="me-1"
        data-testid={`line-edit-${line._id}`}
        onClick={() => handleStartEdit(line)}
        disabled={loading}
        title="Correct this line"
      >
        <i className="fa fa-pen" />
      </Button>
      <Button
        size="sm"
        variant="outline-danger"
        data-testid={`line-delete-${line._id}`}
        onClick={() => { setError(''); setDeleting(line); }}
        disabled={loading}
        title="Remove this line"
      >
        <i className="fa fa-trash" />
      </Button>
    </>
  ) : (
    <Button
      size="sm"
      variant="outline-warning"
      data-testid={`line-reverse-${line._id}`}
      onClick={() => { setError(''); setReverseReason(''); setReversing(line); }}
      disabled={loading}
      title={`${describeLocks(lineLocks)} — reverse it instead`}
    >
      <i className="fa fa-rotate-left me-1" />
      Reverse
    </Button>
  ));

  return (
    <>
      <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
        {/* The ledger is being read or written. Named so a test can pin the
            thing the user actually waits on. */}
        {loading && <i className="fa fa-spinner fa-spin text-muted" data-testid="ledger-busy" />}
        <Button size="sm" variant="outline-secondary" onClick={window.print} disabled={loading}>
          <i className="fa fa-print me-1" />
          Print
        </Button>
      </div>
      <Error error={error} setError={setError} />

      <OrderLedger
        order={order}
        lines={orderLines}
        balanceByOperator={balanceByOperator}
        locks={locks}
        payments={linesLoaded ? payments : null}
        renderActions={readOnly ? null : renderLineActions}
      />

      {/* Add-adjustment form — separate card so the two-sided model
        (customer vs operator) reads cleanly. Customer amount is
        booking-wide. Operator amount requires picking an operator,
        because there's no coherent meaning of a "booking-wide
        operator" adjustment.
        Hidden in readOnly mode (Month End's drill-down reads, never
        writes).
        When the booking is Completed the form remains visible but
        every input + the submit button is disabled, with an inline
        note explaining why. All other statuses (Cancelled, Confirmed,
        Request, Pending Ingestion, Driver No-Show, Passenger
        No-Show, …) allow edits. */}
      {!readOnly && (() => {
        const isLockedByStatus = order.status === 'Completed';
        const consequences = describeConsequences(newLine, {
          operatorName: chosenOperatorName,
        });
        return (
          <Card className="mt-3" ref={editFormRef} border={editingId ? 'primary' : undefined}>
            <Card.Header>
              <i className="fa fa-pen me-2" />
              {editingId ? 'Edit adjustment' : 'Add manual adjustment'}
            </Card.Header>
            <Card.Body>
              {isLockedByStatus && (
                <div
                  className="text-muted small mb-3"
                  data-testid="adjustments-locked-note"
                >
                  <i className="fa fa-lock me-1" />
                  Booking completed — adjustments are locked
                </div>
              )}
              {prefillNote && (
                <div
                  className="alert alert-info small py-2 mb-3"
                  data-testid="adjustments-prefill-note"
                >
                  <i className="fa fa-info-circle me-1" />
                  {prefillNote}
                </div>
              )}
              {/* controlId rather than a bare id: it ties the label to
                  the box, so a screen reader announces it and a click on
                  the word "Description" lands the cursor. */}
              <Form.Group className="mb-3" controlId="description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  ref={editFirstFieldRef}
                  type="text"
                  placeholder="What is this adjustment for? (shows in audit log)"
                  value={newLine.description}
                  onChange={(e) => setStateFromInput(e, setNewLine, newLine)}
                  disabled={isLockedByStatus}
                />
              </Form.Group>

              {/* Currency comes BEFORE the amounts and is repeated inside
                  each box, because the unit is the expensive thing to get
                  wrong here: 800 typed against a EUR line instead of a HUF
                  one is off by the FX rate, not by a rounding. */}
              <Row className="g-3 mb-3">
                <Col md={4}>
                  <Form.Group controlId="currency">
                    <Form.Label>Currency</Form.Label>
                    <Form.Select
                      value={newLine.currency}
                      onChange={(e) => setStateFromInput(e, setNewLine, newLine)}
                      disabled={isLockedByStatus}
                    >
                      {currencyOptions.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Both amounts below are in this currency. This booking is in
                      {' '}
                      {bookingCurrency}
                      .
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Two sides, said out loud. The boxes used to sit next to
                  each other labelled only "Customer amount adjustment" and
                  "Operator amount adjustment", with nothing on screen
                  saying that one is what we charge and the other is what we
                  pay out — so which box a parking receipt belonged in was a
                  guess. */}
              <Row className="g-3">
                <Col md={6}>
                  <div
                    className="border rounded h-100 p-3"
                    data-testid="adjustment-customer-side"
                  >
                    <div className="fw-semibold">
                      <i className="fa fa-user me-2 text-secondary" />
                      Customer side — what we charge
                    </div>
                    <div className="text-muted small mb-3">
                      Money between us and whoever pays for the booking.
                      Changes the booking total.
                    </div>
                    <Form.Group controlId="customer_adjustment">
                      <Form.Label className="small mb-1">Customer amount</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="0"
                          value={newLine.customer_adjustment}
                          onChange={(e) => setStateFromInput(e, setNewLine, newLine)}
                          disabled={isLockedByStatus}
                        />
                        <InputGroup.Text data-testid="customer-amount-currency">
                          {newLine.currency}
                        </InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted d-block">
                        <strong>+</strong>
                        {' '}
                        charge the customer more ·
                        {' '}
                        <strong>−</strong>
                        {' '}
                        refund or discount them
                      </Form.Text>
                    </Form.Group>
                  </div>
                </Col>

                {/* Operator amount + picker are coupled: an operator must
                    be selected BEFORE an amount can be entered, otherwise
                    it's ambiguous who the credit applies to. Picker is
                    the driving field; amount is disabled until then. */}
                <Col md={6}>
                  <div
                    className="border rounded h-100 p-3"
                    data-testid="adjustment-operator-side"
                  >
                    <div className="fw-semibold">
                      <i className="fa fa-car me-2 text-secondary" />
                      Operator side — what we pay out
                    </div>
                    <div className="text-muted small mb-3">
                      Money between us and the driver, guide or supplier.
                      Does not change the booking total.
                    </div>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="target_operator_id" className="small mb-1">
                        For operator
                        {hasOperators && <span className="text-danger ms-1">*</span>}
                      </Form.Label>
                      {/* Scoped, searchable, and grouped by WHAT each entry
                          is. The unfiltered list of every operator is how an
                          expense ended up on the wrong record; cost buckets
                          are offered only for the vehicles our own drivers
                          on this booking are running. */}
                      <Typeahead
                        id="adjustment-operator-picker"
                        inputProps={{ id: 'target_operator_id' }}
                        labelKey="label"
                        options={operatorOptions}
                        selected={selectedOperatorOption ? [selectedOperatorOption] : []}
                        onChange={(picked) => setNewLine((prev) => ({
                          ...prev,
                          target_operator_id: picked[0] ? String(picked[0].id) : '',
                        }))}
                        renderMenu={renderOperatorMenu}
                        placeholder={hasOperators
                          ? 'Type a name or plate to search…'
                          : 'No operators on this booking'}
                        emptyLabel="No match on this booking or its vehicles."
                        disabled={!hasOperators || isLockedByStatus}
                        minLength={0}
                        paginate={false}
                        clearButton
                      />
                      <Form.Text className="text-muted d-block">
                        {hasOperators
                          ? 'This booking\'s operators and the cost buckets for its vehicles only.'
                          : 'No operators on this booking — operator-side adjustments unavailable.'}
                      </Form.Text>
                    </Form.Group>
                    <Form.Group controlId="operator_adjustment">
                      <Form.Label className="small mb-1">Operator amount</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="0"
                          value={newLine.operator_adjustment}
                          onChange={(e) => setStateFromInput(e, setNewLine, newLine)}
                          disabled={!newLine.target_operator_id || isLockedByStatus}
                        />
                        <InputGroup.Text data-testid="operator-amount-currency">
                          {newLine.currency}
                        </InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted d-block">
                        {newLine.target_operator_id ? (
                          <>
                            <strong>+</strong>
                            {' '}
                            we owe them more ·
                            {' '}
                            <strong>−</strong>
                            {' '}
                            they paid us back / settled cash
                          </>
                        ) : 'Pick an operator above first.'}
                      </Form.Text>
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              <Row className="mt-3 align-items-end g-3">
                <Col className="text-end">
                  {editingId && (
                    <Button
                      variant="outline-secondary"
                      className="me-2"
                      onClick={handleCancelEdit}
                      disabled={loading}
                      data-testid="adjustment-cancel-edit"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={loading || isLockedByStatus}
                  >
                    <i className={`fa ${editingId ? 'fa-check' : 'fa-plus'} me-1`} />
                    {editingId ? 'Save changes' : 'Add adjustment'}
                  </Button>
                </Col>
              </Row>

              {/* What this will actually DO, before it is saved. A
                  mis-attribution is free to fix while it is still on
                  screen; afterwards it is an edit at best and a reversal
                  at worst. */}
              {consequences.length > 0 && (
                <div
                  className="alert alert-secondary small py-2 mt-3 mb-0"
                  data-testid="adjustment-consequences"
                >
                  <i className="fa fa-arrow-right me-1" />
                  {consequences.map((sentence) => (
                    <div key={sentence}>{sentence}</div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        );
      })()}

      {/* Deleting an adjustment is irreversible in the ledger sense — the
          line is gone, not struck through — so it is confirmed. Only ever
          offered on a line nothing references. */}
      <Modal show={!!deleting} onHide={() => setDeleting(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete this adjustment?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            <strong>{deleting?.description}</strong>
          </p>
          <p className="text-muted small mb-0">
            Nothing has paid or billed this line, so it can be removed
            outright rather than cancelled out with a negative one.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleting(null)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={loading}
            data-testid="confirm-delete-adjustment"
          >
            Delete line
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reversal of a settled line. The reason is required because it is
          what the struck-through row says — without it the pair reads as a
          bare negative again. */}
      <Modal show={!!reversing} onHide={() => setReversing(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Reverse this adjustment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            <strong>{reversing?.description}</strong>
          </p>
          <p className="text-muted small">
            This line has been paid or billed, so it stays. A matching
            opposite line will be created for you — same amount, operator and
            currency — and the two will show as one struck-through row.
          </p>
          <Form.Group>
            <Form.Label>Reason</Form.Label>
            <Form.Control
              type="text"
              id="reverse_reason"
              data-testid="reverse-reason"
              placeholder="e.g. wrong operator"
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReversing(null)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleReverse}
            disabled={loading || !reverseReason.trim()}
            data-testid="confirm-reverse-adjustment"
          >
            Reverse line
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default OrderLedgerTab;
